import { ggVars, toCamelCase } from 'constants/ggVars';

import { SchemaObject } from 'ajv';

export const SCHEMA_BASE = 'https://schemas.gastrograph.com/report-params/v2/';
export const COMMON_SCHEMA_ID = `${SCHEMA_BASE}common.json`;

/** `$ref` into the shared definitions. */
export const ref = (name: string): SchemaObject => ({
  $ref: `common.json#/$defs/${name}`,
});

export const bool: SchemaObject = { type: 'boolean' };
export const nullType: SchemaObject = { type: 'null' };
export const nullable = (schema: SchemaObject): SchemaObject => ({
  anyOf: [schema, nullType],
});
export const int = (minimum?: number, maximum?: number): SchemaObject => ({
  type: 'integer',
  ...(minimum !== undefined && { minimum }),
  ...(maximum !== undefined && { maximum }),
});
export const num = (minimum?: number, maximum?: number): SchemaObject => ({
  type: 'number',
  ...(minimum !== undefined && { minimum }),
  ...(maximum !== undefined && { maximum }),
});
export const stringList = (
  options: { minItems?: number; maxItems?: number; items?: SchemaObject } = {},
): SchemaObject => ({
  type: 'array',
  items: options.items ?? ref('nonEmptyString'),
  uniqueItems: true,
  ...(options.minItems !== undefined && { minItems: options.minItems }),
  ...(options.maxItems !== undefined && { maxItems: options.maxItems }),
});

export const GGVAR_KEYS = ggVars.map((g) => toCamelCase(g.key));

/**
 * Definitions shared by every report type. Bounds come from profiling the
 * params of every successful report run in the last two years.
 */
export const commonSchema: SchemaObject = {
  $id: COMMON_SCHEMA_ID,
  $defs: {
    uuid: { type: 'string', format: 'uuid' },
    nonEmptyString: { type: 'string', minLength: 1, pattern: '\\S' },
    jobDefinition: {
      enum: ['gg-report-main', 'gg-report-beta', 'gg-report-staging', 'gg-report-dev'],
    },
    numCores: { enum: [7, 8, 10, 15, 16, 31, 63] },
    projectName: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      pattern: '^[A-Za-z0-9][A-Za-z0-9_\\- ]*$',
    },
    clientName: { type: 'string', minLength: 1, maxLength: 256, pattern: '\\S' },
    targetGroupName: { type: 'string', minLength: 1, pattern: '\\S' },
    workspaceId: { type: 'integer', minimum: 1 },
    requestedBy: { type: 'string', minLength: 1 },
    /** client/project/target_group/YYYY-MM-DD_HH-MM-SS, lower-cased, spaces -> _ */
    artifactPath: {
      type: 'string',
      pattern: '^[^/\\s]+/[^/\\s]+/[^/\\s]+/\\d{4}-\\d{2}-\\d{2}_\\d{2}-\\d{2}-\\d{2}/?$',
    },
    fraction: { type: 'number', minimum: 0, maximum: 1 },
    countryList: stringList({ minItems: 1, maxItems: 1, items: { type: 'string', pattern: '^[a-z_ ]+$' } }),
    ageRange: {
      type: 'object',
      additionalProperties: false,
      required: ['min', 'max', 'fraction'],
      properties: {
        min: { type: 'integer', minimum: 8, maximum: 98 },
        max: { type: 'integer', minimum: 9, maximum: 99 },
        fraction: { type: 'number', exclusiveMinimum: 0, maximum: 1 },
      },
      orderedPair: ['min', 'max'],
    },
    ageRanges: {
      type: 'array',
      minItems: 1,
      maxItems: 5,
      items: { $ref: '#/$defs/ageRange' },
      fractionsSumTo: 1,
    },
    productList: stringList({ minItems: 1, maxItems: 500 }),
    categoryList: stringList({ maxItems: 100 }),
    workspaceList: stringList({ minItems: 1 }),
    smokingHabits: { enum: ['All', 'Smokers', 'Non-Smokers'] },
    constraintLevel: { enum: ['UHC', 'VHC', 'HC', 'MC', 'LC', 'VLC', 'ULC', 'NC'] },
    ggvarKey: { enum: GGVAR_KEYS },
    ggvarValue: { type: 'number', minimum: 0, maximum: 5 },
    tuningFraction: { type: 'number', minimum: 0, maximum: 0.25 },
    reportIdToInherit: nullable({ type: 'string', format: 'uuid' }),
    customClassDbName: nullable({ type: 'string', maxLength: 256 }),
  },
};
